import {
  ChangeEvent,
  FormEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import AppContext from '../../contexts/AppContext';
import ConfigContext from '../../contexts/ConfigContext';
import DataContext from '../../contexts/DataContext';

import Button from '../../components/Button';
import Input from '../../components/Input';
import { formatPhoneNumber } from '../../helpers';

import Slide from '../components/Slide';
import { contact } from '../data/pages';

interface FormElements extends HTMLFormControlsCollection {
  fullName: HTMLInputElement;
  phone: HTMLInputElement;
}

interface ContactFormElement extends HTMLFormElement {
  readonly elements: FormElements;
}

export default function Contact() {
  const { urlParams, showClickToCall } = useContext(AppContext);
  const { isDebrandedVersion, features } = useContext(ConfigContext);
  const { data, updateData } = useContext(DataContext);

  const [nameHaveError, setNameHaveError] = useState(false);
  const [phoneHaveError, setPhoneHaveError] = useState(false);
  const navigate = useNavigate();
  const titleRef: any = useRef(null);

  const { fullName, phone } = data;
  const { button, heading, inputData, privacyPolicyUrl, tosUrl } = contact;
  const isButtonDisabled =
    !(fullName && phone) || nameHaveError || phoneHaveError;

  useEffect(() => {
    setTimeout(
      titleRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'center',
      })
    );
  }, [titleRef]);

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const name = event.target.value.trim();
    const nameArray = name.split(' ');

    if (nameArray.length >= 2) {
      updateData({
        ...data,
        fullName: name,
      });
      setNameHaveError(false);
    } else {
      updateData({
        ...data,
        fullName: null,
      });
      setNameHaveError(true);
    }
  };

  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    const number = event.target.value.trim();
    const realNumber = number.replace(/-|\(|\)|\s/g, '');

    if (realNumber.length >= 10) {
      if (realNumber.length > 10) {
        return;
      }
      setPhoneHaveError(false);
      const formattedPhoneNumber = formatPhoneNumber(number);
      updateData({
        ...data,
        phone: formattedPhoneNumber,
      });
    } else {
      setPhoneHaveError(true);
      const formattedPhoneNumber = formatPhoneNumber(number);
      updateData({
        ...data,
        phone: formattedPhoneNumber,
      });
    }
  };

  const handleSubmit = (event: FormEvent<ContactFormElement>) => {
    event.preventDefault();

    dataLayer.push({ event: 'seform_contact_information_entered' });

    if (process.env.REACT_APP_ENV !== 'local') {
      const lastSubmittedTime = localStorage.getItem('slf-last-submitted');
      if (lastSubmittedTime) {
        const now = Date.now();
        const secondsPassed = (now - Number(lastSubmittedTime)) / 1000;
        if (secondsPassed < 60 * 5) return;
      }
      localStorage.setItem('slf-last-submitted', String(Date.now()));
    }

    // On form submit success, navigate to success page
    navigate(`/calculating${urlParams}`);
  };

  return (
    <>
      <Slide heading={heading} titleRef={titleRef} />
      <form onSubmit={handleSubmit}>
        <p className="mt-[30px] text-[17px] short:!text-[16px] md:text-[18px]">
          {inputData.fullName.label}
        </p>
        <Input
          className="mt-3 md:mt-4 mb-6 md:mb-6 md:w-[400px]"
          errorMessage={inputData.fullName.errorMessage}
          haveError={nameHaveError}
          name={inputData.fullName.name}
          onChange={handleNameChange}
          placeholder={inputData.fullName.placeholder}
          value={fullName}
        />
        <p className="mt-[20px] md:mt-6 text-[17px] short:!text-[16px] md:text-[18px]">
          {inputData.phone.label}
        </p>
        <Input
          className="mt-3 md:mt-4 mb-6 md:mb-10 md:w-[400px]"
          errorMessage={inputData.phone.errorMessage}
          haveError={phoneHaveError}
          name={inputData.phone.name}
          onChange={handlePhoneChange}
          placeholder={inputData.phone.placeholder}
          type={inputData.phone.type}
          value={phone}
        />
        {showClickToCall ||
        ['ca', 'tx', 'fl'].includes(data.selectedState.toLowerCase()) ? (
          <p className="text-[11px] md:text-xs mr-0 md:mr-[80px]">
            <label>
              <input type="hidden" id="leadid_tcpa_disclosure" />* We’re here to
              help! Each time that you fill out this form, you agree to be
              contacted by phone call, pre-recorded voice, and/or text message
              at the telephone or mobile number that you entered above by
              Enphase Energy, Inc. and by{' '}
              <a
                href="/solar-installers/"
                target="_blank"
                rel="noreferrer"
                className="underline">
                one (1) other company
              </a>{' '}
              about solar and energy storage products and services. These
              calls/messages may be sent using automated telephone technology,
              even if your telephone or mobile number is currently listed on any
              state, federal or corporate “Do Not Call” list. Message and data
              rates may apply. You are not required to give your consent here as
              a condition of any purchase.
            </label>{' '}
            I understand the{' '}
            <a
              href={`${isDebrandedVersion ? '/tos#privacy' : privacyPolicyUrl}`}
              target="_blank"
              rel="noreferrer"
              className="underline">
              Privacy Policy
            </a>{' '}
            and agree to the{' '}
            <a
              href={`${isDebrandedVersion ? '/tos' : tosUrl}`}
              target="_blank"
              rel="noreferrer"
              className="underline">
              Terms & Conditions
            </a>
            .
          </p>
        ) : (
          <p className="text-[11px] md:text-xs mr-0 md:mr-[80px]">
            <label>
              <input type="hidden" id="leadid_tcpa_disclosure" />*  We’re here to
              help! Each time that you fill out this form, you agree to be
              contacted by phone call, pre-recorded voice, and/or text message
              at the telephone or mobile number that you entered above by
              Enphase Energy, Inc. and by{' '}
              <a
                href="/solar-installers/"
                target="_blank"
                rel="noreferrer"
                className="underline">
                one (1) other company
              </a>{' '}
              about solar and energy storage products and services. These
              calls/messages may be sent using automated telephone technology,
              even if your telephone or mobile number is currently listed on any
              state, federal or corporate “Do Not Call” list. Message and data
              rates may apply. You are not required to give your consent here as
              a condition of any purchase.
            </label>{' '}
            I understand the{' '}
            <a
              href={`${isDebrandedVersion ? '/tos#privacy' : privacyPolicyUrl}`}
              target="_blank"
              rel="noreferrer"
              className="underline">
              Privacy Policy
            </a>{' '}
            and agree to the{' '}
            <a
              href={`${isDebrandedVersion ? '/tos' : tosUrl}`}
              target="_blank"
              rel="noreferrer"
              className="underline">
              Terms & Conditions
            </a>
            .
          </p>
        )}

        <div className="mt-[18px] md:mt-[40px]">
          <Button
            disabled={isButtonDisabled}
            title={button.title}
            type={button.type}
           // data-zuko-id="contact_continue"
          />
        </div>
      </form>
    </>
  );
}
